import {
  ReactNode,
  UIEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import strings from "../../../strings";
import { TextChatMessage, TextChatMessageListItem } from "../TextChat.types";
import s from "./TextChatMessageList.module.css";
import {
  getMessageRangeChange,
  getNewMessageScrollDecision,
  getScrollTopAfterPrepend,
  groupTextChatMessagesByDate,
  isNearTextChatBottom,
  TextChatScrollSnapshot,
} from "./TextChatMessageList.utils";

type TextChatMessageListProps = {
  conversationKey?: string | null;
  messages: TextChatMessage[];
  renderMessage: (item: TextChatMessageListItem) => ReactNode;
  canLoadOlder?: boolean;
  canLoadNewer?: boolean;
  isLoadingOlder?: boolean;
  isLoadingNewer?: boolean;
  onLoadOlder?: () => Promise<void> | void;
  onLoadNewer?: () => Promise<void> | void;
  onScrollToLatest?: () => Promise<void> | void;
  locale?: string;
  newMessageLabel?: string;
  loadingOlderLabel?: string;
  className?: string;
  bottomThresholdPx?: number;
  prefersReducedMotion?: boolean;
  hideDateSeparators?: boolean;
};

const getSnapshot = (element: HTMLDivElement): TextChatScrollSnapshot => ({
  scrollTop: element.scrollTop,
  scrollHeight: element.scrollHeight,
  clientHeight: element.clientHeight,
});

const TextChatMessageList = ({
  conversationKey,
  messages,
  renderMessage,
  canLoadOlder = false,
  canLoadNewer = false,
  isLoadingOlder = false,
  isLoadingNewer = false,
  onLoadOlder,
  onLoadNewer,
  onScrollToLatest,
  locale,
  newMessageLabel = strings.globalChatScrollToLatest,
  loadingOlderLabel = strings.loading,
  className,
  bottomThresholdPx,
  prefersReducedMotion = false,
  hideDateSeparators = false,
}: TextChatMessageListProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const activeConversationKeyRef = useRef<string | null | undefined>(
    conversationKey,
  );
  const latestRequestedConversationKeyRef = useRef<string | null | undefined>(
    null,
  );
  const previousMessageCountRef = useRef(0);
  const previousFirstMessageIdRef = useRef<string | undefined>(undefined);
  const previousLastMessageIdRef = useRef<string | undefined>(undefined);
  const prependSnapshotRef = useRef<TextChatScrollSnapshot | null>(null);
  const newerAppendPendingRef = useRef(false);
  const scrollToLatestPendingRef = useRef(false);
  const scrollToLatestBehaviorRef = useRef<ScrollBehavior>("smooth");
  const wasNearBottomRef = useRef(true);
  const hasInitializedScrollRef = useRef(false);
  const [showNewMessageAffordance, setShowNewMessageAffordance] =
    useState(false);
  const groupedMessages = useMemo(
    () => groupTextChatMessagesByDate(messages, locale),
    [messages, locale],
  );

  const scrollToBottom = (behavior: ScrollBehavior = "auto") => {
    bottomRef.current?.scrollIntoView({ behavior });
    setShowNewMessageAffordance(false);
  };

  const handleScrollToLatest = async () => {
    prependSnapshotRef.current = null;
    newerAppendPendingRef.current = false;
    scrollToLatestPendingRef.current = true;
    scrollToLatestBehaviorRef.current = prefersReducedMotion
      ? "auto"
      : "smooth";
    await onScrollToLatest?.();

    if (!canLoadNewer && !isLoadingNewer) {
      scrollToLatestPendingRef.current = false;
      scrollToBottom(scrollToLatestBehaviorRef.current);
    }
  };

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const element = event.currentTarget;
    const snapshot = getSnapshot(element);
    const nearBottom = isNearTextChatBottom(snapshot, bottomThresholdPx);

    wasNearBottomRef.current = nearBottom;

    if (nearBottom) {
      setShowNewMessageAffordance(false);
    } else {
      setShowNewMessageAffordance(true);
    }

    if (
      element.scrollTop === 0 &&
      canLoadOlder &&
      !isLoadingOlder &&
      onLoadOlder
    ) {
      prependSnapshotRef.current = snapshot;
      onLoadOlder();
    }

    if (nearBottom && canLoadNewer && !isLoadingNewer && onLoadNewer) {
      newerAppendPendingRef.current = true;
      onLoadNewer();
    }
  };

  useLayoutEffect(() => {
    const element = scrollContainerRef.current;

    if (!element) {
      return;
    }

    const conversationChanged =
      activeConversationKeyRef.current !== conversationKey;

    if (conversationChanged) {
      activeConversationKeyRef.current = conversationKey;
      prependSnapshotRef.current = null;
      newerAppendPendingRef.current = false;
      scrollToLatestPendingRef.current = Boolean(canLoadNewer);
      scrollToLatestBehaviorRef.current = "auto";
      wasNearBottomRef.current = true;
      hasInitializedScrollRef.current = false;
      previousMessageCountRef.current = 0;
      previousFirstMessageIdRef.current = undefined;
      previousLastMessageIdRef.current = undefined;
      latestRequestedConversationKeyRef.current = null;
      setShowNewMessageAffordance(false);
    }

    const previousCount = previousMessageCountRef.current;
    const previousFirstId = previousFirstMessageIdRef.current;
    const previousLastId = previousLastMessageIdRef.current;
    const nextFirstId = messages[0]?.id;
    const nextLastId = messages[messages.length - 1]?.id;
    const rangeChange = getMessageRangeChange({
      previousCount,
      nextCount: messages.length,
      previousFirstId,
      nextFirstId,
      previousLastId,
      nextLastId,
    });
    const shouldInitializeScroll =
      !hasInitializedScrollRef.current && rangeChange === "initial";

    if (scrollToLatestPendingRef.current && !canLoadNewer && !isLoadingNewer) {
      scrollToLatestPendingRef.current = false;
      prependSnapshotRef.current = null;
      newerAppendPendingRef.current = false;
      scrollToBottom(scrollToLatestBehaviorRef.current);
    } else if (shouldInitializeScroll) {
      scrollToBottom("auto");
      hasInitializedScrollRef.current = true;
    } else if (rangeChange === "prepend" && prependSnapshotRef.current) {
      element.scrollTop = getScrollTopAfterPrepend(
        prependSnapshotRef.current,
        element.scrollHeight,
      );
      prependSnapshotRef.current = null;
    } else if (rangeChange === "append" && newerAppendPendingRef.current) {
      newerAppendPendingRef.current = false;
      setShowNewMessageAffordance(
        !isNearTextChatBottom(getSnapshot(element), bottomThresholdPx),
      );
    } else if (rangeChange === "append") {
      const decision = getNewMessageScrollDecision({
        wasNearBottom: wasNearBottomRef.current,
        prefersReducedMotion,
      });

      if (decision.shouldScrollToBottom) {
        scrollToBottom(decision.behavior);
      }

      setShowNewMessageAffordance(decision.shouldShowNewMessageAffordance);
    }

    previousMessageCountRef.current = messages.length;
    previousFirstMessageIdRef.current = messages[0]?.id;
    previousLastMessageIdRef.current = messages[messages.length - 1]?.id;
  }, [
    bottomThresholdPx,
    canLoadNewer,
    conversationKey,
    isLoadingNewer,
    messages,
    prefersReducedMotion,
  ]);

  useEffect(() => {
    if (
      !conversationKey ||
      !canLoadNewer ||
      isLoadingNewer ||
      latestRequestedConversationKeyRef.current === conversationKey
    ) {
      return;
    }

    latestRequestedConversationKeyRef.current = conversationKey;
    scrollToLatestPendingRef.current = true;
    scrollToLatestBehaviorRef.current = "auto";
    void onScrollToLatest?.();
  }, [canLoadNewer, conversationKey, isLoadingNewer, onScrollToLatest]);

  return (
    <div className={`${s.container} ${className ?? ""}`}>
      <div
        ref={scrollContainerRef}
        className={s.scrollContainer}
        onScroll={handleScroll}
      >
        {isLoadingOlder && (
          <div className={s.loadingOlder} role="status">
            {loadingOlderLabel}
          </div>
        )}

        {groupedMessages.map((group) => (
          <section className={s.dateGroup} key={group.dateKey}>
            {!hideDateSeparators && (
              <div className={s.dateSeparator}>
                <span className={s.dateLine} />
                <span className={s.dateLabel}>{group.label}</span>
                <span className={s.dateLine} />
              </div>
            )}
            {group.messages.map((item) => (
              <div
                className={`${s.messageItem} ${
                  item.isFirstInSenderGroup ? s.firstInSenderGroup : ""
                } ${item.isLastInSenderGroup ? s.lastInSenderGroup : ""}`}
                key={item.message.id}
                data-message-id={item.message.id}
              >
                {renderMessage(item)}
              </div>
            ))}
          </section>
        ))}

        <div ref={bottomRef} />
      </div>

      {showNewMessageAffordance && (
        <button
          type="button"
          className={s.newMessageButton}
          aria-label={newMessageLabel}
          onClick={() => {
            void handleScrollToLatest();
          }}
        >
          {newMessageLabel}
        </button>
      )}
    </div>
  );
};

export default TextChatMessageList;
